import { Button, ScoreRing } from '@qi/ui';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useFunnelScore } from '../lib/funnel-score.ts';
import { useSessionStore } from '../stores/session-store.ts';

export const Route = createFileRoute('/_funnel/start/improved')({
  beforeLoad: () => {
    const { faceSummary, voiceSummary } = useSessionStore.getState();
    if (!faceSummary || !voiceSummary) throw redirect({ to: '/start' });
  },
  component: FunnelImprovedPage,
});

/**
 * Honest "potential" headroom — NOT a measured after-score. The cold funnel
 * can't re-measure post-calibration, so we project the range the user's system
 * could reach with consistent sessions. Lower current reads have more headroom;
 * deterministic so it's stable across re-renders. Capped 5–15%.
 */
function projectPotential(composite: number): number {
  const factor = Math.min(0.15, Math.max(0.05, 0.15 - (composite / 100) * 0.1));
  return Math.min(100, Math.round(composite * (1 + factor)));
}

function FunnelImprovedPage() {
  const navigate = useNavigate();
  const { composite, isLoading } = useFunnelScore();
  const potential = projectPotential(composite);
  const delta = potential - composite;

  if (isLoading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-md items-center justify-center px-6">
        <p className="animate-pulse font-mono text-xs uppercase tracking-[0.32em] text-aurora">
          Mapping your potential…
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-8 px-6 py-12">
      <header className="space-y-3 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-aurora">Your potential</p>
        <h1 className="font-display text-3xl text-ink-0">This is what you can stabilize</h1>
        <p className="text-pretty text-ink-1">
          Based on your read, here's the resonance range your system can reach and hold with
          consistent sessions.
        </p>
      </header>

      <div className="qi-glass rounded-lg p-6">
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4">
          <div className="flex justify-center">
            <ScoreRing score={composite} size={120} thickness={8} label="Today" tone="before" />
          </div>
          <div className="flex flex-col items-center gap-1">
            <span
              className="font-mono text-4xl leading-none tabular-nums text-aurora"
              style={{ textShadow: 'var(--glow-aurora)' }}
            >
              +{delta}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-aurora/80">
              Potential
            </span>
          </div>
          <div className="flex justify-center">
            <ScoreRing score={potential} size={120} thickness={8} label="Potential" tone="after" />
          </div>
        </div>
        <p className="mt-5 border-t border-hairline pt-4 text-center font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
          Projected range with consistent use · not a measured result
        </p>
      </div>

      <div className="space-y-3">
        <Button
          variant="resonance"
          size="lg"
          className="w-full"
          onClick={() => navigate({ to: '/start/paywall' })}
        >
          Unlock my full potential
        </Button>
        <p className="text-center text-xs text-ink-3">
          Keep your read, track every session, and follow the protocol that gets you there.
        </p>
      </div>
    </div>
  );
}
